import { zodResolver } from '@hookform/resolvers/zod'
import type { MutationResponse } from '@console/types'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'
import { useRun } from '../runs/queries.js'
import { useApproveRun, useRefuseRun, useRuleRun } from '../runs/mutations-queries.js'
import { Button, Fingerprint, HairlineRule, Skeleton } from '../ui/index.js'

export type MutateAction = 'approve' | 'refuse' | 'rule'

const COPY: Record<
  MutateAction,
  { title: string; confirmLabel: string; fieldLabel: string | null; successVerb: string }
> = {
  approve: {
    title: 'Approve this run',
    confirmLabel: 'Approve',
    fieldLabel: null,
    successVerb: 'Approved',
  },
  refuse: {
    title: 'Refuse this run',
    confirmLabel: 'Refuse',
    fieldLabel: 'Reason',
    successVerb: 'Refused',
  },
  rule: {
    title: 'Rule on this run',
    confirmLabel: 'Record ruling',
    fieldLabel: 'Ruling',
    successVerb: 'Ruling recorded',
  },
}

/** `approve` has no text field at all, so it can't be required unconditionally — only `refuse`/`rule` need a non-empty value. */
function textFieldSchema(required: boolean) {
  return z.object({
    text: required ? z.string().min(1, 'This field is required.') : z.string().optional(),
  })
}
type TextFieldValues = z.infer<ReturnType<typeof textFieldSchema>>

/**
 * Phase 6, addendum §I: approve, refuse, and rule share one screen and one
 * conflict discipline — the only real differences are copy and whether a
 * text field (reason / ruling) is collected. A fingerprint mismatch renders
 * a dedicated conflict state in place, with a "try again" path that
 * resubmits against the fingerprint Cadence just reported as current —
 * never a silent retry against the stale one.
 */
export function MutateRunPage({ action }: { action: MutateAction }) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const runQuery = useRun(id ?? '')
  const approve = useApproveRun()
  const refuse = useRefuseRun()
  const rule = useRuleRun()

  const [conflict, setConflict] = useState<{ message: string; fingerprint: string } | null>(null)

  const mutation = action === 'approve' ? approve : action === 'refuse' ? refuse : rule
  const copy = COPY[action]

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TextFieldValues>({ resolver: zodResolver(textFieldSchema(copy.fieldLabel !== null)) })

  if (id === undefined) {
    return <p role="alert">No run id in the URL.</p>
  }

  if (runQuery.isPending) {
    return (
      <main style={{ padding: '2rem', maxWidth: '36rem' }}>
        <Skeleton width="40%" />
        <Skeleton />
      </main>
    )
  }

  if (runQuery.isError) {
    return (
      <main style={{ padding: '2rem' }}>
        <p role="alert" style={{ color: 'var(--stop)' }}>
          {runQuery.error.message}
        </p>
      </main>
    )
  }

  const receipt = runQuery.data
  const fingerprintToSubmit = conflict?.fingerprint ?? receipt.fingerprint
  const offered = receipt.availableActions.find((a) => a.action === action)

  /**
   * THE FLOW SURVIVES; THE SUBMIT DOES NOT.
   *
   * The lead's ruling was to keep the Rule flow rather than delete it and
   * rebuild it when CAD-005 lands — so this screen still exists, still loads
   * the run, and still renders. What it will not do is let an operator fill in
   * a ruling, press a button, and receive a 501 for their trouble.
   *
   * Written against `availableActions` rather than `action === 'rule'`, so
   * this is a statement about what Cadence is offering on THIS run, not a
   * hardcoded opinion about one verb. It covers the other honest case too:
   * arriving at `/approve` for a run that is not awaiting anyone, which the
   * server would answer `nothing_to_decide`.
   */
  if (offered === undefined) {
    const isRule = action === 'rule'
    return (
      <main style={{ padding: '2rem', maxWidth: '42rem' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem' }}>{copy.title}</h1>
        <p role="status">
          {isRule
            ? 'Ruling on an ambiguity isn’t available from the console yet — that mechanism has its own shape, still being designed, separate from approving or refusing.'
            : 'This run isn’t waiting on a decision, so there’s nothing to ' +
              `${action} here. It may have been decided already, or it may never have needed you.`}
        </p>
        <p>
          <Link to={`/runs/${receipt.runId}`}>← Back to the run</Link>
        </p>
      </main>
    )
  }

  function submit(values: Partial<TextFieldValues>): void {
    const runId = receipt.runId
    const onDone = (result: MutationResponse): void => {
      if ('error' in result) {
        setConflict({ message: result.message, fingerprint: result.currentReceipt.fingerprint })
        return
      }
      navigate(`/runs/${runId}`)
    }

    if (action === 'approve') {
      approve.mutate({ runId, fingerprint: fingerprintToSubmit }, { onSuccess: onDone })
    } else if (action === 'refuse') {
      refuse.mutate(
        { runId, fingerprint: fingerprintToSubmit, reason: values.text ?? '' },
        { onSuccess: onDone },
      )
    } else {
      rule.mutate(
        { runId, fingerprint: fingerprintToSubmit, ruling: values.text ?? '' },
        { onSuccess: onDone },
      )
    }
  }

  const onSubmit = handleSubmit((values) => {
    submit(values)
  })

  if (conflict) {
    return (
      <main style={{ padding: '2rem', maxWidth: '36rem' }}>
        <h1>This run changed since you loaded it</h1>
        <p role="alert" style={{ color: 'var(--stop)' }}>
          {conflict.message}
        </p>
        <p style={{ color: 'var(--muted)' }}>
          The fingerprint you were about to act on is no longer current — something else moved this
          run first. Its current version is <Fingerprint value={conflict.fingerprint} />. Review the
          run again before deciding whether to proceed.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`/runs/${id}`)
            }}
          >
            View current state
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setConflict(null)
            }}
          >
            Try again with the current version
          </Button>
        </div>
      </main>
    )
  }

  return (
    <main style={{ padding: '2rem', maxWidth: '36rem' }}>
      <h1>{copy.title}</h1>
      <p style={{ color: 'var(--muted)' }}>
        Sealing fingerprint <Fingerprint value={fingerprintToSubmit} />. If this run changes before
        you confirm, the action is refused rather than applied to a version you never saw.
      </p>

      <HairlineRule />

      <form
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        {copy.fieldLabel !== null && (
          <div style={{ margin: '1rem 0' }}>
            <label htmlFor="mutate-text" style={{ display: 'block', marginBottom: '0.25rem' }}>
              {copy.fieldLabel}
            </label>
            <textarea
              id="mutate-text"
              rows={4}
              style={{ width: '100%', fontFamily: 'inherit' }}
              aria-invalid={errors.text !== undefined}
              {...register('text')}
            />
            {errors.text && (
              <p role="alert" style={{ color: 'var(--stop)' }}>
                {errors.text.message}
              </p>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          <Button
            variant="secondary"
            onClick={() => {
              navigate(`/runs/${id}`)
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? `${copy.confirmLabel}…` : copy.confirmLabel}
          </Button>
        </div>

        {mutation.isError && (
          <p role="alert" style={{ color: 'var(--stop)' }}>
            {mutation.error.message}
          </p>
        )}
      </form>
    </main>
  )
}
